import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik, getIn } from "formik";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  TextField,
  Grid,
  InputAdornment,
  useTheme
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ModalElement from "../Modal";

import {
  initInterview,
  recordAnswer,
  fetchMetricStep,
  fetchPersonnelStep,
  fetchOperationalStep
} from "../../actions/interviewActions";

// deep-equality check
const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// build form initial values
const buildEmptyValues = (metrics) =>
  metrics.reduce((acc, m) => {
    acc[m.name] = { value: "", unit: m.unit || "" };
    return acc;
  }, {});

// sync suggestedJson into form
const buildNextValues = (curr, metrics, sj) => {
  const next = { ...curr };
  metrics.forEach((m) => {
    const v = sj[m.name];
    if (v) next[m.name] = { value: v.value ?? "", unit: v.unit ?? m.unit };
  });
  return next;
};

const MessageBubble = ({ role, content }) => {
  const theme = useTheme();
  const isAssistant = role === "assistant";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isAssistant ? "flex-start" : "flex-end",
        mb: 1.5
      }}
    >
      <Box
        sx={{
          maxWidth: "90%",
          bgcolor: isAssistant
            ? theme.palette.background.paper
            : theme.palette.success.main,
          color: isAssistant ? theme.palette.text.primary : "#fff",
          borderRadius: 2,
          px: 2,
          py: 1.2,
          whiteSpace: "pre-wrap",
          boxShadow: isAssistant ? 1 : "none",
          fontSize: "0.95rem"
        }}
      >
        {content}
      </Box>
    </Box>
  );
};

export default function ChatInterviewModal({
  open,
  onClose,
  industry,
  metrics,
  extra_context = "",
  outcome,
  purpose,
  reason,
  mode = "metrics",           // "metrics" | "personnel" | "operational"
  category,
  subjectType,                // employee title or cost item
  driver,
  driverConfig,
  multiple,
  frequency,
  positive_driver,                   // only used in "operational" mode
  FormComponent,
  formProps = {}
}) {
  const dispatch = useDispatch();
  const interview = useSelector((s) => s.interview || {});
  const { history = [], suggestedJson = {}, done, loading, error } = interview;

  const visibleHistory = history.filter(
    (m) => typeof m.content === "string" && m.content.trim() !== ""
  );
  
  // Determine which metrics to show in the form
  const effectiveMetrics = (() => {
    if (mode === "metrics") {
      return interview.metrics?.length ? interview.metrics : metrics;
    }
    // personnel: estimate hours_saved per employee
    if (mode === "personnel") {
      return [
        {
          name: "hours_saved",
          label: "Hours Saved",
          unit: "hours",
          adornment: "end"
        }
      ];
    }
    // operational: estimate cost_saved per multiple unit
    if (mode === "operational") {
      return [
        {
          name: "cost_saved",
          label: "Cost Saved",
          unit: "€",
          adornment: "start"
        }
      ];
    }
    return [];
  })();

  const [input, setInput] = useState("");
  const bottomRef = useRef();
  const initRef = useRef(false);
  const prevJsonRef = useRef(null);

  // Initialize interview chain when modal opens
  useEffect(() => {
    if (open && !initRef.current) {
      initRef.current = true;
      prevJsonRef.current = null;
      switch (mode) {
        case "metrics":
          dispatch(initInterview(industry, outcome, purpose, reason, metrics, extra_context, mode));
          break;
        case "personnel":
          dispatch(initInterview(industry, { outcome, category, subjectType, driver }, mode));
          break;
        case "operational":
          dispatch(initInterview(industry, { outcome, category, subjectType, driver, multiple, frequency }, mode));
          break;
      }
    }
    if (!open) {
      initRef.current = false;
    }
  }, [open, dispatch, industry, metrics, extra_context, outcome, reason, category, subjectType, driver, multiple, mode]);

  // Kick off first question
  useEffect(() => {
    if (!open || !initRef.current || visibleHistory.length > 0 || loading || error) {
      return;
    }
    switch (mode) {
      case "metrics":
        dispatch(fetchMetricStep(industry, outcome, purpose, reason, [], effectiveMetrics, extra_context));
        break;
      case "personnel":
        dispatch(fetchPersonnelStep(industry, outcome, [], category, subjectType, driver, positive_driver));
        break;
      case "operational":
        dispatch(fetchOperationalStep(industry, outcome, [], category, subjectType, driver, multiple, frequency, positive_driver));
        break;
    }
  }, [
    open,
    visibleHistory.length,
    loading,
    error,
    dispatch,
    industry,
    effectiveMetrics,
    outcome,
    reason,
    category,
    subjectType,
    driver,
    multiple,
    frequency,
    mode
  ]);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [visibleHistory]);

  // Formik setup
  const formik = useFormik({
    initialValues: buildEmptyValues(effectiveMetrics),
    enableReinitialize: false,
    onSubmit: (vals) => {
      console.log("Submitted values:", vals);
      onClose();
    }
  });

  // Sync AI suggestions into form
  useEffect(() => {
    if (!done) return;
    if (!suggestedJson || !Object.keys(suggestedJson).length) return;
    if (deepEqual(prevJsonRef.current, suggestedJson)) return;

    const next = buildNextValues(formik.values, effectiveMetrics, suggestedJson);
    if (!deepEqual(next, formik.values)) {
      formik.setValues(next, false);
    }
    prevJsonRef.current = suggestedJson;
  }, [done, suggestedJson, effectiveMetrics, formik]);

  // Handle sending user replies
  const sendMessage = () => {
    if (!input.trim()) return;
    dispatch(recordAnswer(input));
    const newHistory = [...history, { role: "user", content: input }];
    switch (mode) {
      case "metrics":
        dispatch(fetchMetricStep(industry, outcome, purpose, reason, newHistory, effectiveMetrics, extra_context));
        break;
      case "personnel":
        dispatch(fetchPersonnelStep(
          industry,
          outcome,
          newHistory,
          category,
          subjectType,
          driver,
          positive_driver
        ));
        break;
      case "operational":
        dispatch(fetchOperationalStep(
          industry,
          outcome,
          newHistory,
          category,
          subjectType,
          driver,
          multiple,
          frequency,
          positive_driver
        ));
        break;
    }
    setInput("");
  };

  return (
    <ModalElement
      modalOpen={open}
      handleClose={onClose}
      header="Ask AI"
      instruction={
        mode === "metrics"
          ? "Answer the context question(s); the AI will populate the form."
          : mode === "personnel"
            ? `Answer the AI's questions to help estimate ${positive_driver? "hours saved": "extra hours"} per employee.`
            : `Answer the AI's questions to help estimate ${positive_driver? "cost savings": "extra cost"} per ${multiple}.`
      }
      modalWidth={1100}
      modalHeight="90vh"
      content={
        <Grid container spacing={2} sx={{ height: "100%", mt: 1, maxHeight: "calc(90vh - 64px)" }}>
          {/* Chat panel */}
          <Grid item xs={12} md={7} sx={{ display: "flex", flexDirection: "column", maxHeight: "100%", minHeight: 0 }}>
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                p: 2,
                overflowY: "auto",
                bgcolor: "#f7f7f8",
                borderRadius: 2,
                maxHeight: "70vh",
                minHeight: 0
              }}
            >
              {visibleHistory.map((m, i) => <MessageBubble key={i} {...m} />)}

              {done && Object.keys(suggestedJson || {}).length > 0 && (
                <MessageBubble
                  role="assistant"
                  content="I've populated the form with my estimates. You can adjust if needed."
                />
              )}

              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {error && (
                <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
                  {error}
                </Typography>
              )}

              <div ref={bottomRef} />
            </Paper>

            {!done && (
              <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Type your answer…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    !e.shiftKey &&
                    (e.preventDefault(), sendMessage())
                  }
                  disabled={loading}
                  multiline
                  maxRows={4}
                />
                <IconButton
                  color="primary"
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                  sx={{ alignSelf: "flex-end" }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            )}
          </Grid>

          {/* Form panel */}
          <Grid item xs={12} md={5} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Paper variant="outlined" sx={{ p: 2, flex: 1, overflowY: "auto", borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <SmartToyOutlinedIcon fontSize="small" /> Estimated Values
              </Typography>
              {FormComponent && (
                <React.Fragment
                  key={JSON.stringify(suggestedJson)}
                >
                  <FormComponent
                    {...formProps}
                    category={category}
                    type={subjectType}
                    driverKey={driver}
                    multiple={multiple}
                    frequency={frequency}
                    positiveDriver={positive_driver}
                    iconName={driver}
                    driverConfig={driverConfig}
                    suggestedJson={suggestedJson}
                    done={done}
                    onSubmit={(payload, helpers) => formProps.onSubmit(payload, helpers)}
                  />
                </React.Fragment>
              )}
            </Paper>
          </Grid>
        </Grid>
      }
    />
  );
}
